/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Grid,
  Box,
  Typography,
  Container,
  useTheme,
  useMediaQuery,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import CustomRadioGroup from "@components/CustomRadioGroup";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { useEffect, useState } from "react";
import { CargoPublicoData, DependenciasData, PersonaStructure, RelationshipData } from "@interfaces/index";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import dayjs from "dayjs";
import { InactivityHandler } from "@components/InactivityHandler";
import { PEP_CODES } from "@helpers/constantes";
import { UUID } from "crypto";
import CustomDatePicker from "@components/CustomDatePicker";
import { separarNombres } from "@helpers/funciones";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import {
  useLazyGetPersonByIdGuaranteeQuery,
  useLazyGetPersonPepsSecondGuaranteeQuery,
  usePatchPersonSecondGuaranteeMutation,
  usePostPersonPepsSecondGuaranteeMutation,
} from "@api/second_guarantee_api/secondGuaranteeApi";
import {
  useLazyGetCargoPublicoSecondGuaranteeQuery,
  useLazyGetDependeciasSecondGuaranteeQuery,
  useLazyGetPepsTypeSecondGuaranteeQuery,
  useLazyGetRelationshipSecondGuaranteeQuery,
} from "@api/second_guarantee_api/catalogsSecondGuarantee";

const InformacionSegundoAvalP2 = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const personAval = useSelector((state: RootState) => state.secondGuarante.guarantee);

  const [patchPerson] = usePatchPersonSecondGuaranteeMutation();
  const [getPerson] = useLazyGetPersonByIdGuaranteeQuery();

  const [postPersonPeps] = usePostPersonPepsSecondGuaranteeMutation();
  const [getPersonPeps] = useLazyGetPersonPepsSecondGuaranteeQuery();
  const [getDependeciasLista] = useLazyGetDependeciasSecondGuaranteeQuery();
  const [getCargoPublico] = useLazyGetCargoPublicoSecondGuaranteeQuery();
  const [getPepsType] = useLazyGetPepsTypeSecondGuaranteeQuery();
  const [getFamiliar] = useLazyGetRelationshipSecondGuaranteeQuery();

  const [listDependecias, setListDependecias] = useState<DependenciasData[]>([]);
  const [listCargo, setListCargo] = useState<CargoPublicoData[]>([]);
  const [listFamiliar, setListFamiliar] = useState<RelationshipData[]>([]);
  const [avalSelected, setAvalSelected] = useState<PersonaStructure | null>(null);
  const [idPepTypePropio, setIdPepTypePropio] = useState<UUID | null>(null);
  const [idPepTypeFamiliar, setIdPepTypeFamiliar] = useState<UUID | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      acutuas: "",
      recursos: "",
      publicas: "",
      actualmente: "",
      conyuge: "",
      dependencia: "",
      cargo: "",
      start_date: "",
      end_date: "",
      peps_conyuge: [
        {
          parentesco_conyuge: "",
          nombre_conyuge: "",
          ap_paterno_conyuge: "",
          ap_materno_conyuge: "",
          dependencia_conyuge: "",
          cargo_conyuge: "",
          actualmente_conyuge: "",
          start_date_conyuge: "",
          end_date_conyuge: "",
          id: "",
        },
      ],
    },
    validationSchema: yup.object().shape({
      acutuas: yup.string().required("Campo requerido"),
      recursos: yup.string().required("Campo requerido"),
      publicas: yup.string().required("Campo requerido"),
      conyuge: yup.string().required("Campo requerido"),
      dependencia: yup.string().when("publicas", {
        is: "true",
        then: (schema) => schema.required("Campo requerido"),
      }),
      cargo: yup.string().when("publicas", {
        is: "true",
        then: (schema) => schema.required("Campo requerido"),
      }),
      actualmente: yup.string().when("publicas", {
        is: "true",
        then: (schema) => schema.required("Campo requerido"),
      }),
      start_date: yup.string().when("publicas", {
        is: "true",
        then: (schema) =>
          schema.required("Campo requerido").matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido"),
      }),
      end_date: yup
        .string()
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
        .when("actualmente", {
          is: "false",
          then: (schema) =>
            schema
              .required("Campo requerido")
              .test(
                "end_date_greater_than_start_date",
                "La fecha de término no puede ser menor que la fecha de inicio",
                function (value) {
                  const { start_date } = this.parent;
                  if (start_date && value) {
                    return dayjs(value).isSameOrAfter(dayjs(start_date));
                  }
                  return true;
                },
              )
              .test("end_date_not_in_future", "La fecha de término no puede ser mayor al día actual", (value) => {
                if (value) {
                  return dayjs(value).isSameOrBefore(dayjs(), "day");
                }
                return true;
              }),
        }),
      peps_conyuge: yup.array().of(
        yup.object().shape({
          parentesco_conyuge: yup
            .string()
            .when("conyuge", {
              is: "true",
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            })
            .when("$conyuge", ([conyuge], schema) =>
              conyuge === "true" ? schema.required("Campo requerido") : schema.notRequired(),
            ),
          nombre_conyuge: yup
            .string()
            .when("conyuge", {
              is: "true",
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            })
            .when("$conyuge", ([conyuge], schema) =>
              conyuge === "true" ? schema.required("Campo requerido") : schema.notRequired(),
            ),
          ap_paterno_conyuge: yup
            .string()
            .when("conyuge", {
              is: "true",
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            })
            .when("$conyuge", ([conyuge], schema) =>
              conyuge === "true" ? schema.required("Campo requerido") : schema.notRequired(),
            ),
          ap_materno_conyuge: yup
            .string()
            .when("conyuge", {
              is: "true",
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            })
            .when("$conyuge", ([conyuge], schema) =>
              conyuge === "true" ? schema.required("Campo requerido") : schema.notRequired(),
            ),
          dependencia_conyuge: yup
            .string()
            .when("conyuge", {
              is: "true",
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            })
            .when("$conyuge", ([conyuge], schema) =>
              conyuge === "true" ? schema.required("Campo requerido") : schema.notRequired(),
            ),
          cargo_conyuge: yup
            .string()
            .when("conyuge", {
              is: "true",
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            })
            .when("$conyuge", ([conyuge], schema) =>
              conyuge === "true" ? schema.required("Campo requerido") : schema.notRequired(),
            ),
          actualmente_conyuge: yup
            .string()
            .when("conyuge", {
              is: "true",
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            })
            .when("$conyuge", ([conyuge], schema) =>
              conyuge === "true" ? schema.required("Campo requerido") : schema.notRequired(),
            ),
          start_date_conyuge: yup
            .string()
            .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
            .when("conyuge", {
              is: "true",
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            })
            .when("$conyuge", ([conyuge], schema) =>
              conyuge === "true" ? schema.required("Campo requerido") : schema.notRequired(),
            ),
          end_date_conyuge: yup
            .string()
            .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
            .when("actualmente_conyuge", {
              is: "false",
              then: (schema) =>
                schema
                  .required("Campo requerido")
                  .test(
                    "end_date_greater_than_start_date",
                    "La fecha de término no puede ser menor que la fecha de inicio",
                    function (value) {
                      const { start_date_conyuge } = this.parent;
                      if (start_date_conyuge && value) {
                        return dayjs(value).isSameOrAfter(dayjs(start_date_conyuge), "day");
                      }
                      return true;
                    },
                  )
                  .test("end_date_not_in_future", "La fecha de término no puede ser mayor al día actual", (value) => {
                    if (value) {
                      return dayjs(value).isSameOrBefore(dayjs(), "day");
                    }
                    return true;
                  }),
              otherwise: (schema) => schema.notRequired(),
            }),
        }),
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!avalSelected || !idPepTypePropio || !idPepTypeFamiliar) {
          return;
        }

        setSubmitting(true);
        const payload_peps: { peps: any[] } = {
          peps: [],
        };

        if (values.publicas) {
          payload_peps.peps.push({
            loan_application_id: String(personAval?.loan_application_id),
            person_id: avalSelected.id,
            pep_type_id: idPepTypePropio,
            name: `${avalSelected.name} ${avalSelected.last_name} ${avalSelected.last_name_2}`,
            position: values.cargo || null,
            dependency: values.dependencia || null,
            active: values.actualmente === "true",
            start_date: values.start_date || null,
            end_date: values.end_date || null,
            relationship_id: null,
          });
        }

        if (values.conyuge) {
          values.peps_conyuge.forEach((item) => {
            payload_peps.peps.push({
              loan_application_id: String(personAval?.loan_application_id),
              person_id: avalSelected.id,
              pep_type_id: idPepTypeFamiliar,
              name: `${item.nombre_conyuge} ${item.ap_paterno_conyuge} ${item.ap_materno_conyuge}`,
              position: item.cargo_conyuge || null,
              dependency: item.dependencia_conyuge || null,
              active: item.actualmente_conyuge === "true",
              start_date: item.start_date_conyuge || null,
              end_date: item.end_date_conyuge || null,
              relationship_id: item.parentesco_conyuge || null,
            });
          });
        }

        await postPersonPeps({
          personId: avalSelected.id,
          body: payload_peps,
        }).unwrap();

        await patchPerson({
          personId: avalSelected.id,
          body: {
            own_account: values.acutuas === "true" ? true : false,
            own_resources: values.recursos === "true",
            pep_shareholder: values.publicas === "true",
            pep_members: values.conyuge === "true",
            personal_information_status: "COMPLETO",
          },
        }).unwrap();

        navigate("/avales/expediente_digital_segundo_aval");
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionSegundoAvalP2",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const getFieldError = (fieldName: string, index: number) => {
    const error: any = formik.errors.peps_conyuge?.[index];
    if (error && fieldName in error) {
      return error[fieldName];
    }
    return undefined;
  };

  const handleAddConyuge = () => {
    if (formik.values.peps_conyuge.length < 5) {
      formik.setFieldValue("peps_conyuge", [
        ...formik.values.peps_conyuge,
        {
          parentesco_conyuge: "",
          nombre_conyuge: "",
          ap_paterno_conyuge: "",
          ap_materno_conyuge: "",
          dependencia_conyuge: "",
          cargo_conyuge: "",
          actualmente_conyuge: "",
          start_date_conyuge: "",
          end_date_conyuge: "",
        },
      ]);
    }
  };

  const handleRemoveConyuge = async (index: number) => {
    const updatedPepsConyuge = formik.values.peps_conyuge.filter((_, i) => i !== index);
    formik.setFieldValue("peps_conyuge", updatedPepsConyuge);
    if (updatedPepsConyuge.length === 0) {
      formik.setFieldValue("conyuge", "false");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const data_avales = await getPerson(String(personAval.id)).unwrap();

        const aval_selected = data_avales;

        setAvalSelected(aval_selected);

        formik.setFieldValue("acutuas", String(aval_selected.own_account ?? ""));
        formik.setFieldValue("recursos", String(aval_selected.own_resources ?? ""));
        formik.setFieldValue("publicas", String(aval_selected.pep_shareholder ?? ""));
        formik.setFieldValue("conyuge", String(aval_selected.pep_members ?? ""));

        const [data_peps_type, data_familiar, data_cargo, data_dependencia, data_peps_aval] = await Promise.all([
          getPepsType().unwrap(),
          getFamiliar().unwrap(),
          getCargoPublico().unwrap(),
          getDependeciasLista().unwrap(),
          getPersonPeps({
            personId: aval_selected.id,
          }).unwrap(),
        ]);

        const pep_type_propio = data_peps_type.find((item) => item.name === PEP_CODES.propio.name);
        const pep_type_familiar = data_peps_type.find((item) => item.name === PEP_CODES.familiar.name);

        setListDependecias(data_dependencia);
        setListCargo(data_cargo);
        setListFamiliar(data_familiar);
        setIdPepTypePropio(pep_type_propio?.id || null);
        setIdPepTypeFamiliar(pep_type_familiar?.id || null);

        const peps_propio = data_peps_aval.find((item) => item.pep_type_id === pep_type_propio?.id);
        const peps_familiares = data_peps_aval.filter((item) => item.pep_type_id === pep_type_familiar?.id);

        if (aval_selected.pep_shareholder && peps_propio) {
          formik.setFieldValue("dependencia", peps_propio.dependency);
          formik.setFieldValue("cargo", peps_propio.position);
          formik.setFieldValue("actualmente", !peps_propio.end_date ? "true" : "false");
          formik.setFieldValue(
            "start_date",
            peps_propio.start_date ? dayjs(peps_propio.start_date).format("YYYY-MM-DD") : "",
          );
          formik.setFieldValue(
            "end_date",
            peps_propio.end_date ? dayjs(peps_propio.end_date).format("YYYY-MM-DD") : "",
          );
        }

        if (aval_selected.pep_members && peps_familiares.length > 0) {
          const peps_conyuge: any = [];
          peps_familiares.forEach((item) => {
            const nombreCompleto = separarNombres(item.name || "");
            peps_conyuge.push({
              parentesco_conyuge: item.relationship_id,
              nombre_conyuge: nombreCompleto.nombre,
              ap_paterno_conyuge: nombreCompleto.apellido1 || "",
              ap_materno_conyuge: nombreCompleto.apellido2 || "",
              dependencia_conyuge: item.dependency,
              cargo_conyuge: item.position,
              actualmente_conyuge: item.active ? "true" : "false",
              start_date_conyuge: dayjs(item.start_date).format("YYYY-MM-DD"),
              end_date_conyuge: item.end_date ? dayjs(item.end_date).format("YYYY-MM-DD") : "",
            });
          });
          formik.setFieldValue("peps_conyuge", peps_conyuge);
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionAvalP2-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (formik.values.peps_conyuge.length === 0 && formik.values.conyuge === "true") {
      formik.setFieldValue("peps_conyuge", [
        ...formik.values.peps_conyuge,
        {
          nombre_conyuge: "",
          ap_paterno_conyuge: "",
          ap_materno_conyuge: "",
          dependencia_conyuge: "",
          cargo_conyuge: "",
          actualmente_conyuge: "",
          start_date_conyuge: "",
          end_date_conyuge: "",
          id: "",
        },
      ]);
    }
  }, [formik.values.conyuge, formik.values.peps_conyuge]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información del aval
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
          >
            <strong>2</strong> de 2
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            {avalSelected?.name} {avalSelected?.last_name} {avalSelected?.last_name_2}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: 2, mb: 2 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Box textAlign={"left"} my={2}>
                <CustomRadioGroup
                  label={"¿Actúas por cuenta propio o a nombre de un tercero?"}
                  name={"acutuas"}
                  value={formik.values.acutuas}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.acutuas && Boolean(formik.errors.acutuas)}
                  helperText={formik.touched.acutuas && formik.errors.acutuas}
                  disabled={isLoading}
                  options={[
                    {
                      label: "Propia",
                      value: "true",
                    },
                    {
                      label: "Tercero",
                      value: "false",
                    },
                  ]}
                />
              </Box>

              <Box textAlign={"left"} my={2}>
                <CustomRadioGroup
                  label={"¿Los recursos son propios o de un tercero?"}
                  name={"recursos"}
                  value={formik.values.recursos}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.recursos && Boolean(formik.errors.recursos)}
                  helperText={formik.touched.recursos && formik.errors.recursos}
                  disabled={isLoading}
                  options={[
                    {
                      label: "Propios",
                      value: "true",
                    },
                    {
                      label: "Tercero",
                      value: "false",
                    },
                  ]}
                />
              </Box>

              <Box textAlign={"left"} my={2}>
                <CustomRadioGroup
                  label={
                    "¿Desempeñas o has desempeñado funciones públicas destacadas en México o el extranjero, " +
                    "altos puestos ejecutivos en empresas estatales o funciones importantes en partidos políticos?​"
                  }
                  name={"publicas"}
                  value={formik.values.publicas}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.publicas && Boolean(formik.errors.publicas)}
                  helperText={formik.touched.publicas && formik.errors.publicas}
                  disabled={isLoading}
                  options={[
                    {
                      label: "Si",
                      value: "true",
                    },
                    {
                      label: "No",
                      value: "false",
                    },
                  ]}
                />
              </Box>

              {formik.values.publicas === "true" && (
                <>
                  <FormControl
                    fullWidth
                    required
                    sx={{
                      ...customStylesAsterisk,
                      mt: 2,
                    }}
                    error={formik.touched.dependencia && Boolean(formik.errors.dependencia)}
                  >
                    <InputLabel id="dependencia">Dependencia del cargo</InputLabel>
                    <Select
                      labelId="dependencia"
                      id="dependencia"
                      name="dependencia"
                      fullWidth
                      required
                      label="Dependencia del cargo"
                      value={formik.values.dependencia}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={formik.touched.dependencia && Boolean(formik.errors.dependencia)}
                      disabled={isLoading}
                    >
                      <MenuItem value={""}>Selecciona</MenuItem>
                      {listDependecias.map((item) => (
                        <MenuItem key={item.id} value={item.name}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.dependencia && formik.errors.dependencia && (
                      <FormHelperText>{formik.errors.dependencia}</FormHelperText>
                    )}
                  </FormControl>

                  <FormControl
                    fullWidth
                    required
                    sx={{
                      ...customStylesAsterisk,
                      mt: 2,
                    }}
                    error={formik.touched.cargo && Boolean(formik.errors.cargo)}
                  >
                    <InputLabel id="cargo">Cargo público</InputLabel>
                    <Select
                      labelId="cargo"
                      id="cargo"
                      name="cargo"
                      fullWidth
                      required
                      label="Cargo público"
                      value={formik.values.cargo}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={formik.touched.cargo && Boolean(formik.errors.cargo)}
                      disabled={isLoading}
                    >
                      <MenuItem value={""}>Selecciona</MenuItem>
                      {listCargo.map((item) => (
                        <MenuItem key={item.id} value={item.name}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.cargo && formik.errors.cargo && (
                      <FormHelperText>{formik.errors.cargo}</FormHelperText>
                    )}
                  </FormControl>
                </>
              )}

              {formik.values.publicas === "true" && (
                <>
                  <Box textAlign={"left"} my={2}>
                    <CustomRadioGroup
                      label={"¿Actualmente esta en ese cargo?​"}
                      name={"actualmente"}
                      value={formik.values.actualmente}
                      onChange={(e: any) => {
                        formik.handleChange(e);

                        if (e.target.value === "true") {
                          formik.setFieldValue(`end_date`, "");
                        }
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.actualmente && Boolean(formik.errors.actualmente)}
                      helperText={formik.touched.actualmente && formik.errors.actualmente}
                      disabled={isLoading}
                      options={[
                        {
                          label: "Si",
                          value: "true",
                        },
                        {
                          label: "No",
                          value: "false",
                        },
                      ]}
                    />
                  </Box>

                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <CustomDatePicker
                      label="Fecha de Inicio"
                      name={"start_date"}
                      value={formik.values.start_date}
                      onChange={formik.handleChange}
                      maxDate={dayjs()}
                      onBlur={formik.handleBlur}
                      error={formik.touched.start_date && Boolean(formik.errors.start_date)}
                      helperText={formik.touched.start_date && formik.errors.start_date}
                      disabled={isLoading}
                    />
                  </FormControl>

                  {formik.values.actualmente === "false" && (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <CustomDatePicker
                        label="Fecha de termino"
                        name={"end_date"}
                        value={formik.values.end_date}
                        onChange={formik.handleChange}
                        minDate={formik.values.start_date ? dayjs(formik.values.start_date) : undefined}
                        maxDate={dayjs()}
                        onBlur={formik.handleBlur}
                        error={formik.touched.end_date && Boolean(formik.errors.end_date)}
                        helperText={formik.touched.end_date && formik.errors.end_date}
                        disabled={isLoading}
                      />
                    </FormControl>
                  )}
                  <hr />
                </>
              )}

              <Box textAlign={"left"} my={2}>
                <CustomRadioGroup
                  label={
                    "¿Tu cónyuge o alguno de tus padres, abuelos, hijos, nietos, hermanos, " +
                    "suegros, hijos políticos, abuelos políticos o cuñados, desempeña o ha " +
                    "desempeñado funciones públicas destacadas en México o el extranjero, altos puestos ejecutivos " +
                    "en empresas estatales o funciones importantes en partidos políticos?​"
                  }
                  name={"conyuge"}
                  value={formik.values.conyuge}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.conyuge && Boolean(formik.errors.conyuge)}
                  helperText={formik.touched.conyuge && formik.errors.conyuge}
                  disabled={isLoading}
                  options={[
                    {
                      label: "Si",
                      value: "true",
                    },
                    {
                      label: "No",
                      value: "false",
                    },
                  ]}
                />
              </Box>

              {formik.values.conyuge === "true" && (
                <>
                  {formik.values.peps_conyuge.map((pep, index) => (
                    <Box key={index}>
                      <FormControl
                        fullWidth
                        required
                        sx={{
                          ...customStylesAsterisk,
                          mt: 2,
                        }}
                        error={Boolean(
                          formik.touched.peps_conyuge?.[index]?.parentesco_conyuge &&
                            getFieldError("parentesco_conyuge", index),
                        )}
                      >
                        <InputLabel id={`parentesco_conyuge_${index}`}>Parentesco</InputLabel>
                        <Select
                          labelId={`parentesco_conyuge_${index}`}
                          id={`parentesco_conyuge_${index}`}
                          name={`peps_conyuge[${index}].parentesco_conyuge`}
                          fullWidth
                          required
                          label="Parentesco"
                          value={pep.parentesco_conyuge}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          error={Boolean(
                            formik.touched.peps_conyuge?.[index]?.parentesco_conyuge &&
                              getFieldError("parentesco_conyuge", index),
                          )}
                          disabled={isLoading}
                        >
                          <MenuItem value={""}>Selecciona</MenuItem>
                          {listFamiliar.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>

                        {formik.touched.peps_conyuge?.[index]?.parentesco_conyuge &&
                          getFieldError("parentesco_conyuge", index) && (
                            <FormHelperText>{getFieldError("parentesco_conyuge", index)}</FormHelperText>
                          )}
                      </FormControl>

                      <TextField
                        id={`nombre_conyuge_${index}`}
                        name={`peps_conyuge[${index}].nombre_conyuge`}
                        label="Nombre(s)"
                        margin="normal"
                        required
                        fullWidth
                        autoComplete="off"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        error={Boolean(
                          formik.touched.peps_conyuge?.[index]?.nombre_conyuge &&
                            getFieldError("nombre_conyuge", index),
                        )}
                        helperText={
                          formik.touched.peps_conyuge?.[index]?.nombre_conyuge && getFieldError("nombre_conyuge", index)
                        }
                        value={pep.nombre_conyuge}
                        disabled={isLoading}
                      />

                      <TextField
                        id={`ap_paterno_conyuge_${index}`}
                        name={`peps_conyuge[${index}].ap_paterno_conyuge`}
                        label="Apellido paterno"
                        margin="normal"
                        required
                        fullWidth
                        autoComplete="off"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        error={Boolean(
                          formik.touched.peps_conyuge?.[index]?.ap_paterno_conyuge &&
                            getFieldError("ap_paterno_conyuge", index),
                        )}
                        helperText={
                          formik.touched.peps_conyuge?.[index]?.ap_paterno_conyuge &&
                          getFieldError("ap_paterno_conyuge", index)
                        }
                        value={pep.ap_paterno_conyuge}
                        disabled={isLoading}
                      />

                      <TextField
                        id={`ap_materno_conyuge_${index}`}
                        name={`peps_conyuge[${index}].ap_materno_conyuge`}
                        label="Apellido materno"
                        margin="normal"
                        required
                        fullWidth
                        autoComplete="off"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        error={Boolean(
                          formik.touched.peps_conyuge?.[index]?.ap_materno_conyuge &&
                            getFieldError("ap_materno_conyuge", index),
                        )}
                        helperText={
                          formik.touched.peps_conyuge?.[index]?.ap_materno_conyuge &&
                          getFieldError("ap_materno_conyuge", index)
                        }
                        value={pep.ap_materno_conyuge}
                        disabled={isLoading}
                      />

                      <FormControl
                        fullWidth
                        required
                        sx={{
                          ...customStylesAsterisk,
                          mt: 2,
                        }}
                        error={Boolean(
                          formik.touched.peps_conyuge?.[index]?.dependencia_conyuge &&
                            getFieldError("dependencia_conyuge", index),
                        )}
                      >
                        <InputLabel id={`dependencia_conyuge_${index}`}>Dependencia del cargo</InputLabel>
                        <Select
                          labelId={`dependencia_conyuge_${index}`}
                          id={`dependencia_conyuge_${index}`}
                          name={`peps_conyuge[${index}].dependencia_conyuge`}
                          fullWidth
                          required
                          label="Dependencia del cargo"
                          value={pep.dependencia_conyuge}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          error={Boolean(
                            formik.touched.peps_conyuge?.[index]?.dependencia_conyuge &&
                              getFieldError("dependencia_conyuge", index),
                          )}
                          disabled={isLoading}
                        >
                          <MenuItem value={""}>Selecciona</MenuItem>
                          {listDependecias.map((item) => (
                            <MenuItem key={item.id} value={item.name}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.peps_conyuge?.[index]?.dependencia_conyuge &&
                          getFieldError("dependencia_conyuge", index) && (
                            <FormHelperText>{getFieldError("dependencia_conyuge", index)}</FormHelperText>
                          )}
                      </FormControl>

                      <FormControl
                        fullWidth
                        required
                        sx={{
                          ...customStylesAsterisk,
                          mt: 2,
                        }}
                        error={Boolean(
                          formik.touched.peps_conyuge?.[index]?.cargo_conyuge && getFieldError("cargo_conyuge", index),
                        )}
                      >
                        <InputLabel id={`cargo_conyuge_${index}`}>Cargo público</InputLabel>
                        <Select
                          labelId={`cargo_conyuge_${index}`}
                          id={`cargo_conyuge_${index}`}
                          name={`peps_conyuge[${index}].cargo_conyuge`}
                          fullWidth
                          required
                          label="Cargo público"
                          value={pep.cargo_conyuge}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          error={Boolean(
                            formik.touched.peps_conyuge?.[index]?.cargo_conyuge &&
                              getFieldError("cargo_conyuge", index),
                          )}
                          disabled={isLoading}
                        >
                          <MenuItem value={""}>Selecciona</MenuItem>
                          {listCargo.map((item) => (
                            <MenuItem key={item.id} value={item.name}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.peps_conyuge?.[index]?.cargo_conyuge &&
                          getFieldError("cargo_conyuge", index) && (
                            <FormHelperText>{getFieldError("cargo_conyuge", index)}</FormHelperText>
                          )}
                      </FormControl>

                      <Box textAlign={"left"} my={2}>
                        <CustomRadioGroup
                          label={"¿Actualmente esta en ese cargo?​"}
                          name={`peps_conyuge[${index}].actualmente_conyuge`}
                          value={pep.actualmente_conyuge}
                          onChange={(e: any) => {
                            formik.handleChange(e);

                            if (e.target.value === "true") {
                              formik.setFieldValue(`peps_conyuge[${index}].end_date_conyuge`, "");
                            }
                          }}
                          onBlur={formik.handleBlur}
                          error={Boolean(
                            formik.touched.peps_conyuge?.[index]?.actualmente_conyuge &&
                              getFieldError("actualmente_conyuge", index),
                          )}
                          helperText={
                            formik.touched.peps_conyuge?.[index]?.actualmente_conyuge &&
                            getFieldError("actualmente_conyuge", index)
                          }
                          options={[
                            {
                              label: "Si",
                              value: "true",
                            },
                            {
                              label: "No",
                              value: "false",
                            },
                          ]}
                          disabled={isLoading}
                        />
                      </Box>

                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <CustomDatePicker
                          label="Fecha de inicio"
                          name={`peps_conyuge[${index}].start_date_conyuge`}
                          value={pep.start_date_conyuge ? pep.start_date_conyuge : ""}
                          onChange={formik.handleChange}
                          maxDate={dayjs()}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.peps_conyuge?.[index]?.start_date_conyuge &&
                            getFieldError("start_date_conyuge", index)
                          }
                          helperText={
                            formik.touched.peps_conyuge?.[index]?.start_date_conyuge
                              ? getFieldError("start_date_conyuge", index)
                              : ""
                          }
                          disabled={isLoading}
                        />
                      </FormControl>

                      {formik.values.peps_conyuge[index].actualmente_conyuge === "false" && (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                          <CustomDatePicker
                            label="Fecha de termino"
                            name={`peps_conyuge[${index}].end_date_conyuge`}
                            value={pep.end_date_conyuge ? pep.end_date_conyuge : ""}
                            onChange={formik.handleChange}
                            minDate={pep.start_date_conyuge ? dayjs(pep.start_date_conyuge) : undefined}
                            maxDate={dayjs()}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.peps_conyuge?.[index]?.end_date_conyuge &&
                              getFieldError("end_date_conyuge", index)
                            }
                            helperText={
                              formik.touched.peps_conyuge?.[index]?.end_date_conyuge
                                ? getFieldError("end_date_conyuge", index)
                                : ""
                            }
                            disabled={isLoading}
                          />
                        </FormControl>
                      )}

                      <Typography
                        onClick={() => handleRemoveConyuge(index)}
                        color={"#F2704F"}
                        fontSize={"15px"}
                        fontWeight={400}
                        mt={3}
                        sx={{ cursor: "pointer", textDecoration: "" }}
                      >
                        - Remover persona
                      </Typography>
                      <hr />
                    </Box>
                  ))}

                  <Typography
                    onClick={handleAddConyuge}
                    color={theme?.palette?.primary?.main}
                    fontSize={"15px"}
                    fontWeight={400}
                    mt={3}
                    sx={{ cursor: formik.values.peps_conyuge.length > 5 ? "not-allowed" : "pointer" }}
                  >
                    + Agregar persona
                  </Typography>
                </>
              )}

              <Grid container columnSpacing={1} my={4}>
                <Grid item xs={6}>
                  <Button
                    variant="blue_outlined"
                    type="button"
                    fullWidth
                    onClick={() => {
                      navigate("/avales/expediente_digital_segundo_aval");
                    }}
                    sx={{ padding: "0px", height: "100%" }}
                    disabled={formik.isSubmitting}
                  >
                    Regresar al inicio
                  </Button>
                </Grid>

                <Grid item xs={6}>
                  <Button fullWidth type="submit" variant="default" disabled={!formik.isValid || formik.isSubmitting}>
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionSegundoAvalP2;
